/**
 * FaceAuthLogin.tsx
 * Find ABHA by mobile + verify via QR-based Face Authentication (login/verification flow).
 *
 * Flow:
 *   1. Enter mobile -> search ABHA IDs linked to that number
 *   2. Select an ABHA from list -> init face auth for that selection -> QR
 *   3. Hospital tablet scans QR -> face capture via RD Service
 *   4. Poll completes -> verify login -> get profile
 *   5. Navigate to success
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAbdm } from '../context/AbdmContext';
import {
    handleSearchAbhaByMobile,
    handleFaceAuthSearchSelect,
    handleFaceAuthPoll,
    handleFaceAuthLoginVerify,
    handleViewProfile,
    AbhaProfileItem,
} from '../../utils/api';
import { extractUserProfile } from '../../utils/helpers';

type Phase = 'search' | 'selectAbha' | 'qr' | 'verifying';

const POLL_INTERVAL = 6000;       // 6 seconds
const POLL_TIMEOUT = 600000;      // 10 minutes
const QR_API = 'https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=';

export const FaceAuthLogin: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { authData } = state;

    const [phase, setPhase] = useState<Phase>('search');

    // Search phase
    const [mobile, setMobile] = useState('');
    const [searchTxnId, setSearchTxnId] = useState('');
    const [accounts, setAccounts] = useState<AbhaProfileItem[]>([]);

    // QR phase
    const [txnId, setTxnId] = useState('');
    const [qrUrl, setQrUrl] = useState('');
    const [selectedAccount, setSelectedAccount] = useState<AbhaProfileItem | null>(null);
    const [pollCount, setPollCount] = useState(0);

    // Shared
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Polling refs
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        };
    }, []);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    }, []);

    // ── Step 1: Search by mobile ─────────────────────────────────────────
    const handleSearch = async () => {
        if (!authData || !/^\d{10}$/.test(mobile)) return;
        setLoading(true);
        setError('');
        try {
            const result = await handleSearchAbhaByMobile(authData, mobile);
            setSearchTxnId(result.txnId);
            if (result.abhaList && result.abhaList.length > 0) {
                setAccounts(result.abhaList);
                setPhase('selectAbha');
            } else {
                setError('No ABHA accounts found for this mobile number.');
            }
        } catch (err) {
            setError((err as Error).message || 'Search failed.');
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Select ABHA -> init face auth ────────────────────────────
    const handleSelectAbha = async (account: AbhaProfileItem, index: number) => {
        if (!authData) return;
        setSelectedAccount(account);
        setLoading(true);
        setError('');
        try {
            const result = await handleFaceAuthSearchSelect(authData, authData.tenantId, searchTxnId, String(index));
            setTxnId(result.txnId);
            setQrUrl(result.qrUrl);
            setPhase('qr');
            startPolling(result.txnId);
        } catch (err) {
            setError((err as Error).message || 'Failed to initiate face auth for selected ABHA.');
        } finally {
            setLoading(false);
        }
    };

    // ── Step 3: QR polling ───────────────────────────────────────────────
    const startPolling = useCallback((pollTxnId: string) => {
        stopPolling();
        setPollCount(0);

        pollIntervalRef.current = setInterval(async () => {
            if (!authData) return;
            try {
                setPollCount(c => c + 1);
                const pollResult = await handleFaceAuthPoll(authData, authData.tenantId, pollTxnId);
                const s = (pollResult.status || '').toUpperCase();

                if (s === 'COMPLETE' || s === 'VERIFIED') {
                    stopPolling();
                    handleQrComplete(pollResult.txnId || pollTxnId);
                } else if (s === 'FAILED') {
                    stopPolling();
                    setError(pollResult.message || 'Face authentication failed. Please try again.');
                }
                // PENDING: keep polling
            } catch (err) {
                console.warn('[FaceAuthLogin] Poll error:', (err as Error).message);
                // Don't stop on transient errors
            }
        }, POLL_INTERVAL);

        // Timeout after 10 minutes
        pollTimeoutRef.current = setTimeout(() => {
            stopPolling();
            setError('Face authentication timed out. Please try again.');
        }, POLL_TIMEOUT);
    }, [authData, stopPolling]);

    // ── Step 4: QR complete -> verify login ──────────────────────────────
    const handleQrComplete = async (completedTxnId: string) => {
        if (!authData) return;
        setPhase('verifying');
        setError('');
        try {
            const result = await handleFaceAuthLoginVerify(authData, authData.tenantId, completedTxnId);

            console.log('[FaceAuthLogin] Raw login verify response:', JSON.stringify(result, null, 2));

            // Flatten nested data if present
            const flat = result?.data || result || {};

            // Extract xToken from multiple possible locations
            const xToken = flat.xToken || flat.token || flat.tokens?.xToken || flat.tokens?.token
                || result?.xToken || result?.token || result?.tokens?.xToken || result?.tokens?.token || '';

            if (xToken) {
                dispatch({ type: 'SET_X_TOKEN', payload: xToken });
            }

            const txn = flat.txnId || result?.txnId;
            if (txn) {
                dispatch({ type: 'SET_TRANSACTION_ID', payload: txn });
            }

            // Try to get full profile via profile API (most reliable)
            let profile = null;
            if (xToken) {
                try {
                    const abhaNum = selectedAccount?.ABHANumber || flat.ABHANumber || flat.abhaNumber || '';
                    profile = await handleViewProfile(authData, xToken, state.authConfig, abhaNum);
                } catch (e) {
                    console.warn('[FaceAuthLogin] handleViewProfile failed:', e);
                }
            }

            // Fallback: try extractUserProfile on the raw result
            if (!profile) {
                profile = extractUserProfile(result);
            }

            // Last resort: build minimal profile from selected account + response
            if (!profile && selectedAccount) {
                profile = {
                    fullName: selectedAccount.name || 'Patient',
                    abhaNumber: selectedAccount.ABHANumber || flat.ABHANumber || flat.abhaNumber || '',
                    abhaAddress: flat.abhaAddress || flat.preferredAbhaAddress || '',
                    status: 'ACTIVE',
                    kycStatus: selectedAccount.kycVerified === 'true' ? 'VERIFIED' : 'PENDING',
                    mobileVerified: true,
                    mobile: mobile || flat.mobile || '',
                    gender: selectedAccount.gender || flat.gender || '',
                    address: flat.address || '',
                };
            }

            if (profile) {
                dispatch({ type: 'SET_USER_PROFILE', payload: profile });
                dispatch({ type: 'SET_STEP', payload: 'success' });
            } else {
                setError('Verification succeeded but profile data could not be retrieved.');
                setPhase('qr');
            }
        } catch (err) {
            console.error('[FaceAuthLogin] Login verify error:', err);
            setError((err as Error).message || 'Login verification failed.');
            setPhase('qr');
        }
    };

    // ── Regenerate QR ────────────────────────────────────────────────────
    const handleRegenerate = async () => {
        if (!selectedAccount || !authData) return;
        setError('');
        const idx = selectedAccount.index ?? accounts.indexOf(selectedAccount);
        try {
            const result = await handleFaceAuthSearchSelect(authData, authData.tenantId, searchTxnId, String(idx >= 0 ? idx : 0));
            setTxnId(result.txnId);
            setQrUrl(result.qrUrl);
            setPollCount(0);
            startPolling(result.txnId);
        } catch (err) {
            setError((err as Error).message || 'Failed to regenerate QR.');
        }
    };

    // ── Navigation ───────────────────────────────────────────────────────
    const goBack = () => {
        stopPolling();
        if (phase === 'qr' || phase === 'verifying') { setPhase('selectAbha'); return; }
        if (phase === 'selectAbha') { setPhase('search'); return; }
        dispatch({ type: 'SET_STEP', payload: 'authType' });
    };

    // ── Search Phase ─────────────────────────────────────────────────────
    if (phase === 'search') {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>🤳</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Find ABHA (Face Auth)</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Search by mobile number, then verify via face scan on hospital tablet
                        </p>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="faceLoginMobile">
                        📱 Patient's Mobile Number <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="faceLoginMobile"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="Enter 10-digit mobile number"
                        value={mobile}
                        onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onKeyDown={e => e.key === 'Enter' && mobile.length === 10 && handleSearch()}
                    />
                    {mobile.length > 0 && mobile.length < 10 && (
                        <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>
                            Must be exactly 10 digits ({mobile.length}/10)
                        </p>
                    )}
                    {mobile.length === 10 && (
                        <p style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 4 }}>
                            ✓ Valid mobile number
                        </p>
                    )}
                </div>

                <button
                    className="btn-primary"
                    disabled={mobile.length !== 10 || loading}
                    onClick={handleSearch}
                    style={{ marginTop: '0.5rem' }}
                >
                    {loading ? '⏳ Searching...' : '🔍 Search ABHA'}
                </button>

                <button
                    className="btn-secondary"
                    onClick={() => dispatch({ type: 'SET_STEP', payload: 'authType' })}
                    style={{ marginTop: '0.5rem' }}
                >
                    ⬅ Back
                </button>
            </div>
        );
    }

    // ── Select ABHA Phase ────────────────────────────────────────────────
    if (phase === 'selectAbha') {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>👤</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Select ABHA Account</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Choose the account to verify via Face Authentication
                        </p>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                    {accounts.length} account{accounts.length !== 1 ? 's' : ''} found for mobile {mobile}
                </div>

                <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {accounts.map((acct, idx) => {
                        const isKyc = acct.kycVerified === 'true';
                        const initial = (acct.name || 'A').charAt(0).toUpperCase();
                        return (
                            <div
                                key={acct.ABHANumber || idx}
                                className="flow-option"
                                onClick={() => !loading && handleSelectAbha(acct, acct.index ?? idx)}
                                style={{ cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem' }}
                            >
                                {/* Avatar */}
                                <div style={{
                                    width: 40, height: 40, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
                                }}>
                                    {initial}
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{acct.name}</div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>
                                        {acct.ABHANumber || 'N/A'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 3,
                                            fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                                            background: isKyc ? '#ecfdf5' : '#fffbeb',
                                            color: isKyc ? '#059669' : '#d97706',
                                            border: `1px solid ${isKyc ? '#a7f3d0' : '#fde68a'}`,
                                            textTransform: 'uppercase',
                                        }}>
                                            {isKyc ? '🛡 Verified' : '⚠ Non-KYC'}
                                        </span>
                                        {acct.gender && (
                                            <span style={{
                                                fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                                                borderRadius: 6, background: '#f1f5f9', color: '#64748b',
                                                border: '1px solid #e2e8f0',
                                            }}>
                                                {acct.gender}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Face auth indicator */}
                                <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>🤳</span>
                            </div>
                        );
                    })}
                </div>

                {loading && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', color: '#0d9488', fontSize: '0.85rem', fontWeight: 600 }}>
                        ⏳ Initiating face authentication...
                    </div>
                )}

                <button
                    className="btn-secondary"
                    onClick={goBack}
                    style={{ marginTop: '0.75rem' }}
                >
                    ⬅ Back to Search
                </button>
            </div>
        );
    }

    // ── QR Phase ─────────────────────────────────────────────────────────
    if (phase === 'qr') {
        const qrImageUrl = qrUrl ? `${QR_API}${encodeURIComponent(qrUrl)}` : '';

        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>📷</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Scan QR on Hospital Tablet</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Verifying: <strong>{selectedAccount?.name}</strong> ({selectedAccount?.ABHANumber})
                        </p>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                {/* QR Code Display */}
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                    {qrImageUrl ? (
                        <div style={{
                            display: 'inline-block', padding: '1rem',
                            background: '#fff', borderRadius: 12,
                            border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}>
                            <img
                                src={qrImageUrl}
                                alt="Face Auth QR Code"
                                style={{ width: 250, height: 250, display: 'block' }}
                            />
                        </div>
                    ) : (
                        <div style={{ padding: '2rem', color: '#64748b' }}>
                            ⏳ Generating QR code...
                        </div>
                    )}

                    {/* Polling status */}
                    <div style={{
                        marginTop: '1rem', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 8, fontSize: '0.85rem', color: '#0d9488', fontWeight: 600,
                    }}>
                        <span style={{ animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>⏳</span>
                        Waiting for face scan... (poll #{pollCount})
                    </div>

                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                        Scan this QR code on the hospital tablet and complete the face verification.
                        <br />The page will update automatically once verification is complete.
                    </p>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                        className="btn-primary"
                        onClick={handleRegenerate}
                        style={{ flex: 1 }}
                    >
                        🔄 Regenerate QR
                    </button>
                    <button
                        className="btn-secondary"
                        onClick={goBack}
                        style={{ flex: 1 }}
                    >
                        ⬅ Back
                    </button>
                </div>
            </div>
        );
    }

    // ── Verifying Phase ──────────────────────────────────────────────────
    if (phase === 'verifying') {
        return (
            <div className="step-container" style={{ textAlign: 'center' }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(13,148,136,0.1), #ccfbf1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '1.5rem auto 1rem', fontSize: '1.8rem',
                }}>
                    ⏳
                </div>

                <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem' }}>Verifying Identity...</h2>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
                    Face scan complete. Fetching profile from ABDM...
                </p>

                {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
            </div>
        );
    }

    return null;
};
